import { useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import type { FloorActor, FloorTodayOverview } from "../../api/shopMode";
import { useT } from "../../i18n/LanguageProvider";
import type { TranslateFn } from "../../i18n/t";
import { ShopFloorIcon } from "./ShopFloorIcon";
import { NO_AMOUNT, money, timeText } from "../../i18n/intl";

/**
 * S5: the shared rule. This passed `undefined` for the locale, so it followed
 * the TABLET's configured language rather than the florist's chosen one.
 */
const euro = money;

function durationLabel(t: TranslateFn, totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${t("profile.hours", { count: hours })} ${t("profile.minutes", { count: minutes })}`;
}

/** S5: 24-hour, per the owner's ruling. `hour: "numeric"` gave "5:30 PM". */
function timeLabel(iso: string | null): string {
  if (!iso) return NO_AMOUNT;
  return timeText(new Date(iso));
}

interface ShopFloorProfilePanelProps {
  actor: FloorActor;
  overview: FloorTodayOverview | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onEndShift: () => Promise<void>;
}

export function ShopFloorProfilePanel({
  actor,
  overview,
  loading,
  error,
  onClose,
  onEndShift,
}: ShopFloorProfilePanelProps) {
  const { t } = useT();
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const [ending, setEnding] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);

  const handleEndShift = async () => {
    setEnding(true);
    setEndError(null);
    try {
      await onEndShift();
    } catch (err: unknown) {
      setEndError(errorText(err, "profile.couldNotEnd"));
      setEnding(false);
    }
  };

  return (
    <div className="shop-floor-profile-workspace">
      <section className="card shop-floor-profile-panel" aria-labelledby="shop-profile-title">
        <header className="shop-floor-profile-panel-header">
          <div>
            <p className="shop-floor-profile-eyebrow">{t("profile.eyebrow")}</p>
            <h2 id="shop-profile-title">{actor.fullName}</h2>
          </div>
          <button
            type="button"
            className="shop-floor-profile-close"
            onClick={onClose}
            aria-label={t("profile.close")}
          >
            ×
          </button>
        </header>

        {error ? <div className="alert error">{error}</div> : null}

        {loading && !overview ? (
          <p className="muted">{t("profile.loading")}</p>
        ) : overview ? (
          <>
            <div className="shop-floor-profile-shift-line">
              <span className="shop-floor-sidebar-info-icon" aria-hidden="true">
                <ShopFloorIcon name="calendar" size={22} />
              </span>
              <div>
                <strong>
                  {overview.activeShift
                    ? t("shift.fromTill", {
                        from: timeLabel(overview.activeShift.actualStart),
                        till: timeLabel(overview.activeShift.plannedEnd),
                      })
                    : overview.latestCompletedShift
                      ? t("shift.endedAt", {
                          time: timeLabel(overview.latestCompletedShift.actualEnd),
                        })
                      : t("profile.noActiveShift")}
                </strong>
                <span>{t("profile.businessDay")}</span>
              </div>
            </div>

            {overview.payShown ? (
              <div className="shop-floor-profile-total-card">
                <span>{t("profile.earnedToday")}</span>
                <strong>{euro(overview.totalEarnedTodayEur)}</strong>
                <small>{t("profile.earnedHint")}</small>
              </div>
            ) : null}

            <div className="shop-floor-profile-stats">
              <div className="shop-floor-profile-stat">
                <span>{t("profile.workedSoFar")}</span>
                <strong>{durationLabel(t, overview.workedMinutesToday)}</strong>
              </div>
              {overview.payShown ? (
                <>
                  <div className="shop-floor-profile-stat">
                    <span>{t("profile.hourlyEarnings")}</span>
                    <strong>{overview.salaried ? t("profile.salaried") : euro(overview.wageEarnedTodayEur)}</strong>
                  </div>
                  <div className="shop-floor-profile-stat">
                    <span>{t("profile.commissionToday")}</span>
                    <strong>{euro(overview.commissionEarnedTodayEur)}</strong>
                  </div>
                </>
              ) : null}
            </div>

            {overview.missingHourlyRate ? (
              <div className="alert error">{t("profile.missingRate")}</div>
            ) : null}

            {endError ? <div className="alert error">{endError}</div> : null}

            {overview.activeShift ? (
              <div className="shop-floor-profile-end-zone">
                {confirmingEnd ? (
                  <>
                    <p>{t("profile.endConfirm")}</p>
                    <div className="shop-floor-profile-end-actions">
                      <button
                        type="button"
                        className="button danger"
                        onClick={() => void handleEndShift()}
                        disabled={ending}
                      >
                        {ending ? t("profile.ending") : t("profile.confirmEnd")}
                      </button>
                      <button
                        type="button"
                        className="button plain"
                        onClick={() => setConfirmingEnd(false)}
                        disabled={ending}
                      >
                        {t("profile.keepWorking")}
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="button danger shop-floor-profile-end-button"
                    onClick={() => setConfirmingEnd(true)}
                  >
                    {t("profile.endShift")}
                  </button>
                )}
              </div>
            ) : null}
          </>
        ) : (
          <p className="muted">{t("profile.unavailable")}</p>
        )}
      </section>
    </div>
  );
}
